import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';

const FALLBACK_ICE_SERVERS = [
  { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] },
];
const AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: 1,
};
const ICE_CACHE_MAX_AGE = 50 * 60 * 1000;
const NAV_HEIGHT = 68;

export function micErrorMessage(err) {
  switch (err?.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'Microphone is blocked. Allow it in browser settings.';
    case 'NotFoundError':
      return 'No microphone found.';
    case 'NotReadableError':
      return 'Microphone is busy in another app.';
    case 'OverconstrainedError':
      return 'Microphone is not compatible.';
    default:
      return typeof window !== 'undefined' && window.isSecureContext === false
        ? 'Voice needs a secure connection.'
        : 'Microphone could not be started.';
  }
}

async function selectedCandidateType(pc) {
  try {
    const stats = await pc.getStats();
    let pairId;
    stats.forEach((r) => {
      if (r.type === 'transport' && r.selectedCandidatePairId) pairId = r.selectedCandidatePairId;
    });
    let pair = pairId && stats.get(pairId);
    if (!pair) {
      stats.forEach((r) => {
        if (!pair && r.type === 'candidate-pair' && (r.nominated || r.selected)) pair = r;
      });
    }
    const local = pair && stats.get(pair.localCandidateId);
    return local?.candidateType || 'unknown';
  } catch (_) {
    return 'unknown';
  }
}

export default function WhotVoiceChat({
  socket,
  matchId,
  playerId,
  players = [],
  onVoiceStateChange,
  compact = false,
  connected = true,
  active = true,
  onVoiceShutdownReady,
  gameContainerRef,
}) {
  const [enabled, setEnabled] = useState(false);
  const [muted, setMuted] = useState(false);
  const [status, setStatus] = useState('Off');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [permissionError, setPermissionError] = useState('');
  const [voiceError, setVoiceError] = useState('');
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [micMuted, setMicMuted] = useState(false);
  const [peerStates, setPeerStates] = useState({});
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [hasMic, setHasMic] = useState(false);
  const [speakerMuted, setSpeakerMuted] = useState(false);

  const streamRef = useRef(null);
  const peersRef = useRef(new Map());
  const remoteAudiosRef = useRef(new Map());
  const pendingCandidatesRef = useRef(new Map());
  const analyserRef = useRef(null);
  const speakingTimerRef = useRef(null);
  const speakingRef = useRef(false);
  const latestSpeakingRef = useRef(false);
  const lastSpeakingSentRef = useRef(false);
  const speakingTrailingRef = useRef(null);
  const enabledRef = useRef(false);
  const startingRef = useRef(false);
  const requestingMicRef = useRef(false);
  const mutedRef = useRef(false);
  const micMutedRef = useRef(false);
  const hasMicRef = useRef(false);
  const micWantedRef = useRef(false);
  const speakerMutedRef = useRef(false);
  const onVoiceStateChangeRef = useRef(onVoiceStateChange);
  const playerIdRef = useRef(playerId);
  const connectedRef = useRef(connected);
  const activeRef = useRef(active);
  const failsRef = useRef(0);
  const lastErrorNameRef = useRef('');
  const restartCountRef = useRef(new Map());
  const disconnectTimersRef = useRef(new Map());
  const iceConfigRef = useRef({ iceServers: FALLBACK_ICE_SERVERS, fetchedAt: 0 });
  const readyEmitRef = useRef(0);
  const lastSpeakingEmitRef = useRef(0);
  const peerStatesRef = useRef({});
  const makeOfferRef = useRef(null);
  const restartPeerRef = useRef(null);
  const unmountedRef = useRef(false);

  useEffect(() => { playerIdRef.current = playerId; }, [playerId]);
  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => { micMutedRef.current = micMuted; }, [micMuted]);
  useEffect(() => { hasMicRef.current = hasMic; }, [hasMic]);
  useEffect(() => { speakerMutedRef.current = speakerMuted; }, [speakerMuted]);
  useEffect(() => { onVoiceStateChangeRef.current = onVoiceStateChange; }, [onVoiceStateChange]);
  useEffect(() => { peerStatesRef.current = peerStates; }, [peerStates]);
  useEffect(() => { connectedRef.current = connected; }, [connected]);
  useEffect(() => { activeRef.current = active; }, [active]);

  // Stable wrapper so a parent passing a new callback every render does not
  // re-run the socket effect (which tears down all peers).
  const notifyVoiceState = useCallback((...args) => {
    onVoiceStateChangeRef.current?.(...args);
  }, []);

  const recordFail = useCallback((err) => {
    failsRef.current = Math.min(failsRef.current + 1, 999);
    lastErrorNameRef.current = String(err?.name || err?.message || 'Error').slice(0, 80);
  }, []);

  const emitVoiceState = useCallback(async (pc, state) => {
    if (!socket || !matchId || !socket.connected) return;
    const candidateType = await selectedCandidateType(pc);
    socket.emit('voice:state', {
      matchId,
      state,
      candidateType,
      fails: failsRef.current,
      lastError: lastErrorNameRef.current,
    });
  }, [socket, matchId]);

  const updatePeer = useCallback((socketId, patch) => {
    setPeerStates((prev) => ({
      ...prev,
      [socketId]: { ...(prev[socketId] || {}), ...patch },
    }));
  }, []);

  const refreshStatus = useCallback(() => {
    if (!enabledRef.current) {
      setStatus('Off');
      return;
    }
    if (!connectedRef.current) {
      setStatus('Reconnecting...');
      return;
    }
    const states = Array.from(peersRef.current.values()).map((entry) => ({
      connectionState: entry.pc.connectionState,
      iceConnectionState: entry.pc.iceConnectionState,
    }));
    if (states.some(({ connectionState }) => connectionState === 'connected')) {
      setStatus(mutedRef.current ? 'Muted' : 'Live');
      return;
    }
    if (states.some(({ connectionState, iceConnectionState }) =>
      connectionState === 'failed' || iceConnectionState === 'failed'
    )) {
      setStatus('Unavailable');
      return;
    }
    if (states.some(({ connectionState, iceConnectionState }) =>
      connectionState === 'disconnected' || iceConnectionState === 'disconnected'
    )) {
      setStatus('Reconnecting...');
      return;
    }
    if (states.some(({ connectionState, iceConnectionState }) =>
      ['new', 'connecting'].includes(connectionState) ||
      ['new', 'checking'].includes(iceConnectionState)
    )) {
      setStatus('Connecting');
      return;
    }
    setStatus('Waiting for others');
  }, []);

  const tryPlay = useCallback((audio) => {
    if (!audio) return;
    try {
      const p = audio.play();
      if (p?.catch) {
        p.catch((err) => {
          recordFail(err);
          setAudioBlocked(true);
        });
      }
    } catch (err) {
      recordFail(err);
      setAudioBlocked(true);
    }
  }, [recordFail]);

  const resumeAudio = useCallback(() => {
    setAudioBlocked(false);
    remoteAudiosRef.current.forEach((audio) => tryPlay(audio));
    const ctx = analyserRef.current?.ctx;
    if (ctx?.state === 'suspended') {
      ctx.resume().catch((err) => recordFail(err));
    }
  }, [tryPlay, recordFail]);

  const clearDisconnectTimer = useCallback((socketId) => {
    const timer = disconnectTimersRef.current.get(socketId);
    if (timer) clearTimeout(timer);
    disconnectTimersRef.current.delete(socketId);
  }, []);

  const closePeer = useCallback((socketId) => {
    clearDisconnectTimer(socketId);
    const entry = peersRef.current.get(socketId);
    if (entry) {
      try {
        entry.pc.ontrack = null;
        entry.pc.onicecandidate = null;
        entry.pc.onconnectionstatechange = null;
        entry.pc.oniceconnectionstatechange = null;
        entry.pc.close();
      } catch (err) {
        recordFail(err);
      }
      peersRef.current.delete(socketId);
    }
    const audio = remoteAudiosRef.current.get(socketId);
    if (audio) {
      try {
        audio.pause();
        audio.srcObject = null;
        audio.removeAttribute('src');
        audio.remove();
      } catch (err) {
        recordFail(err);
      }
      remoteAudiosRef.current.delete(socketId);
    }
    pendingCandidatesRef.current.delete(socketId);
    restartCountRef.current.delete(socketId);
    setPeerStates((prev) => {
      const next = { ...prev };
      delete next[socketId];
      return next;
    });
    refreshStatus();
  }, [clearDisconnectTimer, recordFail, refreshStatus]);

  const fetchIceServers = useCallback(async (force = false) => {
    const now = Date.now();
    if (!force && iceConfigRef.current.fetchedAt && now - iceConfigRef.current.fetchedAt < ICE_CACHE_MAX_AGE) {
      return iceConfigRef.current.iceServers;
    }
    try {
      // Same base URL and credentials as the rest of the app. A relative
      // fetch hit the page origin, which often has no /api and silently
      // dropped the TURN relay.
      const response = await api.get('/voice/ice-servers', { skipAuthRedirect: true, timeout: 5000 });
      const data = response.data;
      const servers = Array.isArray(data?.iceServers) && data.iceServers.length
        ? data.iceServers
        : FALLBACK_ICE_SERVERS;
      const hasRelay = servers.some((s) => String(Array.isArray(s.urls) ? s.urls.join(',') : s.urls).startsWith('turn'));
      if (!hasRelay) {
        setVoiceError('Voice relay is not configured; players on some networks may not connect.');
      }
      iceConfigRef.current = { iceServers: servers, fetchedAt: now };
      return servers;
    } catch (err) {
      recordFail(err);
      setVoiceError('Voice relay could not be reached; players on some networks may not connect.');
      return iceConfigRef.current.iceServers || FALLBACK_ICE_SERVERS;
    }
  }, [recordFail]);

  const emitSpeaking = useCallback((next, force = false) => {
    latestSpeakingRef.current = !!next;
    const wait = 250 - (Date.now() - lastSpeakingEmitRef.current);
    const send = () => {
      speakingTrailingRef.current = null;
      const value = latestSpeakingRef.current;
      if (value === lastSpeakingSentRef.current) return;
      lastSpeakingEmitRef.current = Date.now();
      lastSpeakingSentRef.current = value;
      if (socket?.connected) {
        socket.emit('voice:speaking', { matchId, speaking: value });
      }
    };
    if (force) {
      if (speakingTrailingRef.current) clearTimeout(speakingTrailingRef.current);
      speakingTrailingRef.current = null;
      const value = latestSpeakingRef.current;
      if (value !== lastSpeakingSentRef.current && socket?.connected) {
        lastSpeakingSentRef.current = value;
        lastSpeakingEmitRef.current = Date.now();
        socket.emit('voice:speaking', { matchId, speaking: value });
      }
      return;
    }
    if (wait <= 0) {
      if (speakingTrailingRef.current) clearTimeout(speakingTrailingRef.current);
      send();
    } else if (!speakingTrailingRef.current) {
      speakingTrailingRef.current = setTimeout(send, wait);
    }
  }, [socket, matchId]);

  const ensurePeer = useCallback((peer) => {
    if (!socket || !peer?.socketId || peer.socketId === socket.id || !enabledRef.current || !activeRef.current) return null;
    const existing = peersRef.current.get(peer.socketId);
    if (existing) {
      if (peer.muted !== undefined) updatePeer(peer.socketId, { muted: !!peer.muted });
      return existing.pc;
    }

    let pc;
    try {
      pc = new RTCPeerConnection({
        iceServers: iceConfigRef.current.iceServers || FALLBACK_ICE_SERVERS,
        iceCandidatePoolSize: 2,
      });
    } catch (err) {
      recordFail(err);
      setVoiceError('Voice connection could not be created.');
      return null;
    }

    const entry = {
      pc,
      audioSender: null,
      remoteDescriptionSet: false,
      makingOffer: false,
      audioReported: false,
      peer,
    };
    peersRef.current.set(peer.socketId, entry);

    try {
      const transceiver = pc.addTransceiver('audio', { direction: 'sendrecv' });
      entry.audioSender = transceiver.sender;
      if (streamRef.current) {
        const track = streamRef.current.getAudioTracks?.()[0];
        if (track) void entry.audioSender.replaceTrack(track).catch((err) => recordFail(err));
      }
    } catch (err) { recordFail(err); }

    const patch = { userId: peer.userId, username: peer.username, state: 'connecting' };
    if (peer.muted !== undefined) patch.muted = !!peer.muted;
    updatePeer(peer.socketId, patch);

    pc.onicecandidate = (event) => {
      if (event.candidate && socket.connected) {
        socket.emit('voice:ice', { matchId, toSocketId: peer.socketId, candidate: event.candidate });
      }
    };

    pc.ontrack = (event) => {
      let audio = remoteAudiosRef.current.get(peer.socketId);
      let stream = event.streams?.[0];
      if (!stream) {
        // The sender has no associated stream (track is attached later via
        // replaceTrack), so the browser gives an empty streams array. Build
        // one from the track so remote audio still plays.
        stream = audio?.srcObject instanceof MediaStream ? audio.srcObject : new MediaStream();
        if (event.track && !stream.getTracks().includes(event.track)) stream.addTrack(event.track);
      }
      if (!audio) {
        audio = document.createElement('audio');
        audio.autoplay = true;
        audio.playsInline = true;
        audio.volume = 1;
        audio.muted = speakerMutedRef.current;
        audio.setAttribute('aria-hidden', 'true');
        audio.style.display = 'none';
        document.body.appendChild(audio);
        remoteAudiosRef.current.set(peer.socketId, audio);
      }
      if (audio.srcObject !== stream) audio.srcObject = stream;
      tryPlay(audio);
      if (!entry.audioReported) {
        entry.audioReported = true;
        void emitVoiceState(pc, pc.connectionState);
      }
    };

    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      entry.state = state;
      updatePeer(peer.socketId, { state });
      void emitVoiceState(pc, state);
      if (state === 'connected') {
        clearDisconnectTimer(peer.socketId);
        restartCountRef.current.delete(peer.socketId);
        setVoiceError('');
      } else if (state === 'disconnected') {
        clearDisconnectTimer(peer.socketId);
        const timer = setTimeout(() => {
          disconnectTimersRef.current.delete(peer.socketId);
          if (pc.connectionState === 'disconnected') void restartPeerRef.current?.(peer.socketId);
        }, 5000);
        disconnectTimersRef.current.set(peer.socketId, timer);
      } else if (state === 'failed') {
        void restartPeerRef.current?.(peer.socketId);
      } else if (state === 'closed') {
        closePeer(peer.socketId);
      }
      refreshStatus();
    };

    pc.oniceconnectionstatechange = () => {
      entry.iceState = pc.iceConnectionState;
      updatePeer(peer.socketId, { iceState: pc.iceConnectionState });
      if (pc.iceConnectionState === 'failed' && pc.connectionState !== 'failed') void restartPeerRef.current?.(peer.socketId);
      void emitVoiceState(pc, pc.connectionState);
      refreshStatus();
    };

    return pc;
  }, [socket, matchId, updatePeer, tryPlay, emitVoiceState, clearDisconnectTimer, closePeer, refreshStatus, recordFail]);

  const makeOffer = useCallback(async (peer, iceRestart = false) => {
    const pc = ensurePeer(peer);
    if (!pc) return;
    const entry = peersRef.current.get(peer.socketId);
    if (!entry || entry.makingOffer) return;
    if (String(socket.id) > String(peer.socketId)) return;
    entry.makingOffer = true;
    try {
      if (iceRestart) {
        try { pc.restartIce(); } catch (err) { recordFail(err); }
      }
      const offer = await pc.createOffer(iceRestart ? { iceRestart: true } : undefined);
      await pc.setLocalDescription(offer);
      socket.emit('voice:offer', {
        matchId,
        toSocketId: peer.socketId,
        offer: pc.localDescription,
      });
    } catch (err) {
      recordFail(err);
      setVoiceError('Voice connection could not be negotiated.');
    } finally {
      entry.makingOffer = false;
    }
  }, [ensurePeer, socket, matchId, recordFail]);

  const restartPeer = useCallback(async (socketId) => {
    const entry = peersRef.current.get(socketId);
    if (!entry || !socket?.connected) return;
    const n = (restartCountRef.current.get(socketId) || 0) + 1;
    restartCountRef.current.set(socketId, n);
    if (n > 3) {
      // Back off and retry automatically instead of waiting for a manual tap.
      setVoiceError('Voice connection lost. Retrying shortly...');
      setTimeout(() => {
        if (!peersRef.current.has(socketId)) return;
        restartCountRef.current.delete(socketId);
        void restartPeerRef.current?.(socketId);
      }, 30000);
      return;
    }
    if (String(socket.id) < String(socketId)) {
      await makeOfferRef.current?.(entry.peer, true);
    } else if (socket.connected) {
      socket.emit('voice:ready', { matchId, muted: mutedRef.current });
    }
  }, [socket, matchId]);

  useEffect(() => { makeOfferRef.current = makeOffer; }, [makeOffer]);
  useEffect(() => { restartPeerRef.current = restartPeer; }, [restartPeer]);

  const rebuildPeer = useCallback((socketId) => {
    const existing = peersRef.current.get(socketId);
    const peer = existing?.peer || peerStates[socketId];
    if (!peer?.socketId) return;
    restartCountRef.current.delete(socketId);
    closePeer(socketId);
    const rebuilt = ensurePeer(peer);
    if (rebuilt && String(socket.id) < String(socketId)) {
      void makeOffer(peer);
    }
  }, [peerStates, closePeer, ensurePeer, makeOffer, socket]);

  const flushCandidates = useCallback(async (socketId) => {
    const entry = peersRef.current.get(socketId);
    if (!entry?.remoteDescriptionSet) return;
    const pending = pendingCandidatesRef.current.get(socketId) || [];
    pendingCandidatesRef.current.delete(socketId);
    for (const candidate of pending) {
      try {
        await entry.pc.addIceCandidate(candidate);
      } catch (err) {
        recordFail(err);
      }
    }
  }, [recordFail]);

  const stopAnalyser = useCallback(() => {
    if (analyserRef.current?.ctx) analyserRef.current.ctx.close().catch((err) => recordFail(err));
    analyserRef.current = null;
    if (speakingTimerRef.current) cancelAnimationFrame(speakingTimerRef.current);
    speakingTimerRef.current = null;
    speakingRef.current = false;
    latestSpeakingRef.current = false;
    setIsSpeaking(false);
    emitSpeaking(false, true);
  }, [emitSpeaking, recordFail]);

  const startAnalyser = useCallback(async () => {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass || !streamRef.current || analyserRef.current) return;
    try {
      const ctx = new AudioContextClass();
      if (ctx.state === 'suspended') await ctx.resume();
      const source = ctx.createMediaStreamSource(streamRef.current);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      analyserRef.current = { ctx, analyser };
      const data = new Uint8Array(analyser.fftSize);
      const tick = () => {
        if (!streamRef.current || !analyserRef.current) return;
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i += 4) {
          const x = (data[i] - 128) / 128;
          sum += x * x;
        }
        const speaking = Math.sqrt(sum / Math.ceil(data.length / 4)) > 0.055;
        if (speaking !== speakingRef.current) {
          speakingRef.current = speaking;
          latestSpeakingRef.current = speaking;
          setIsSpeaking(speaking);
          emitSpeaking(speaking);
          notifyVoiceState(String(playerIdRef.current), { muted: false, speaking, voiceEnabled: true });
        }
        speakingTimerRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (err) { recordFail(err); }
  }, [emitSpeaking, notifyVoiceState, recordFail]);

  const bindMicTrack = useCallback((track) => {
    track.enabled = true;
    track.onended = () => {
      // Keep micWantedRef true here so the visibility handler can try to
      // recover the mic when the user returns to the tab. It is cleared only
      // when the user turns the mic off or voice shuts down.
      hasMicRef.current = false;
      setHasMic(false);
      setMuted(true);
      mutedRef.current = true;
      peersRef.current.forEach((entry) => entry.audioSender?.replaceTrack(null).catch((err) => recordFail(err)));
      stopAnalyser();
      setVoiceError('Microphone stopped. Tap to enable it again.');
      setMicMuted(false);
      micMutedRef.current = false;
      socket?.emit('voice:mute', { matchId, muted: true });
      notifyVoiceState(String(playerIdRef.current), { muted: true, speaking: false, voiceEnabled: true });
    };
    track.onmute = () => {
      setMicMuted(true);
      micMutedRef.current = true;
    };
    track.onunmute = () => {
      setMicMuted(false);
      micMutedRef.current = false;
    };
  }, [matchId, notifyVoiceState, recordFail, socket, stopAnalyser]);

  const attachMicToPeers = useCallback((track) => {
    peersRef.current.forEach((entry) => {
      try {
        if (entry.audioSender) void entry.audioSender.replaceTrack(track).catch((err) => recordFail(err));
      } catch (err) {
        recordFail(err);
      }
    });
  }, [recordFail]);

  const requestMic = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof RTCPeerConnection === 'undefined') {
      const err = new Error('MEDIA_UNSUPPORTED');
      err.name = 'NotSupportedError';
      setVoiceError(micErrorMessage(err));
      return false;
    }
    if (requestingMicRef.current) return false;
    requestingMicRef.current = true;
    let stream = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: AUDIO_CONSTRAINTS,
        video: false,
      });
      const track = stream.getAudioTracks()[0];
      if (!track) throw Object.assign(new Error('No audio track'), { name: 'NotFoundError' });
      const old = streamRef.current;
      streamRef.current = stream;
      setHasMic(true);
      hasMicRef.current = true;
      micWantedRef.current = true;
      bindMicTrack(track);
      if (old && old !== stream) old.getTracks().forEach((t) => {
        t.onended = null;
        t.onmute = null;
        t.onunmute = null;
        try { t.stop(); } catch (err) { recordFail(err); }
      });
      attachMicToPeers(track);
      setPermissionError('');
      setVoiceError('');
      setMicMuted(false);
      micMutedRef.current = false;
      setMuted(false);
      mutedRef.current = false;
      setEnabled(true);
      enabledRef.current = true;
      setStatus(connectedRef.current ? 'Connecting' : 'Reconnecting...');
      if (socket?.connected) {
        socket.emit('voice:mute', { matchId, muted: false });
        const now = Date.now();
        if (now - readyEmitRef.current > 250) {
          readyEmitRef.current = now;
          socket.emit('voice:ready', { matchId, muted: false });
        }
      }
      notifyVoiceState(String(playerIdRef.current), { muted: false, speaking: false, voiceEnabled: true });
      return true;
    } catch (err) {
      if (stream && stream !== streamRef.current) {
        stream.getTracks().forEach((t) => {
          try { t.stop(); } catch (_) { /* ignore */ }
        });
      }
      recordFail(err);
      const message = micErrorMessage(err);
      setPermissionError(message);
      setVoiceError(message);
      // CRITICAL FIX: Disable voice feature if NotAllowedError (microphone blocked)
      if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') {
        setAudioBlocked(true);
        micWantedRef.current = false;
        enabledRef.current = false;
        setEnabled(false);
      }
      return false;
    } finally {
      requestingMicRef.current = false;
    }
  }, [socket, matchId, bindMicTrack, attachMicToPeers, recordFail, notifyVoiceState]);

  const startVoice = useCallback(async () => {
    if (!socket || !matchId || startingRef.current || !activeRef.current) return;
    startingRef.current = true;
    setPermissionError('');
    setVoiceError('');
    setAudioBlocked(false);
    setEnabled(true);
    enabledRef.current = true;
    setStatus(connectedRef.current ? 'Connecting' : 'Reconnecting...');

    const micOk = await requestMic();
    if (micOk) await startAnalyser();
    else setStatus(connectedRef.current ? 'Listening' : 'Reconnecting...');
    startingRef.current = false;
  }, [requestMic, startAnalyser, socket, matchId]);

  const shutdown = useCallback((emit = true) => {
    if (!enabledRef.current && !streamRef.current && peersRef.current.size === 0) return;
    if (emit && socket?.connected) {
      emitSpeaking(false, true);
      socket.emit('voice:leave', { matchId });
    }
    peersRef.current.forEach((_, socketId) => closePeer(socketId));
    peersRef.current.clear();
    remoteAudiosRef.current.forEach((audio) => {
      try {
        audio.pause();
        audio.srcObject = null;
        audio.remove();
      } catch (err) {
        recordFail(err);
      }
    });
    remoteAudiosRef.current.clear();
    pendingCandidatesRef.current.clear();
    disconnectTimersRef.current.forEach((timer) => clearTimeout(timer));
    disconnectTimersRef.current.clear();
    restartCountRef.current.clear();
    streamRef.current?.getTracks?.().forEach((track) => {
      try { track.stop(); } catch (err) { recordFail(err); }
    });
    streamRef.current = null;
    hasMicRef.current = false;
    micWantedRef.current = false;
    setHasMic(false);
    if (analyserRef.current?.ctx) {
      analyserRef.current.ctx.close().catch((err) => recordFail(err));
    }
    analyserRef.current = null;
    if (speakingTimerRef.current) cancelAnimationFrame(speakingTimerRef.current);
    speakingTimerRef.current = null;
    if (speakingTrailingRef.current) clearTimeout(speakingTrailingRef.current);
    speakingTrailingRef.current = null;
    speakingRef.current = false;
    latestSpeakingRef.current = false;
    lastSpeakingSentRef.current = false;
    enabledRef.current = false;
    startingRef.current = false;
    mutedRef.current = false;
    micMutedRef.current = false;
    setEnabled(false);
    setMuted(false);
    setMicMuted(false);
    setIsSpeaking(false);
    setAudioBlocked(false);
    setStatus('Off');
    setSpeakerMuted(false);
    speakerMutedRef.current = false;
    notifyVoiceState(String(playerIdRef.current), { muted: false, speaking: false, voiceEnabled: false });
  }, [socket, matchId, closePeer, emitSpeaking, notifyVoiceState, recordFail]);

  useEffect(() => {
    onVoiceShutdownReady?.(shutdown);
    return () => onVoiceShutdownReady?.(null);
  }, [onVoiceShutdownReady, shutdown]);

  useEffect(() => {
    if (!active) shutdown(true);
  }, [active, shutdown]);

  useEffect(() => {
    if (!socket || !active || !connected) return undefined;
    // Re-runs of this effect must not force-mute a live mic.
    const micLive = hasMicRef.current && !!streamRef.current;
    enabledRef.current = true;
    setEnabled(true);
    mutedRef.current = !micLive;
    setMuted(!micLive);
    setStatus(micLive ? 'Connecting' : 'Listening');
    const emitReady = () => {
      if (!activeRef.current || !socket.connected) return;
      const now = Date.now();
      if (now - readyEmitRef.current < 250) return;
      readyEmitRef.current = now;
      setStatus('Connecting...');
      peersRef.current.forEach((_, socketId) => closePeer(socketId));
      void fetchIceServers().finally(() => {
        socket.emit('voice:ready', { matchId, muted: mutedRef.current });
      });
    };
    const onConnect = () => emitReady();
    const onPeers = (data) => {
      if (!data?.matchId || String(data.matchId) !== String(matchId) || !enabledRef.current || !connectedRef.current) return;
      (data.peers || []).forEach((peer) => {
        updatePeer(peer.socketId, peer);
        notifyVoiceState(String(peer.userId), { muted: !!peer.muted, speaking: false, voiceEnabled: true });
        ensurePeer(peer);
        if (peer.voiceEnabled && peer.socketId !== socket.id && String(socket.id) < String(peer.socketId)) {
          void makeOffer(peer);
        }
      });
      refreshStatus();
    };
    const onReady = (peer) => {
      if (!enabledRef.current || peer?.socketId === socket.id) return;
      updatePeer(peer.socketId, peer);
      notifyVoiceState(String(peer.userId), { muted: !!peer.muted, speaking: false, voiceEnabled: true });
      ensurePeer(peer);
      if (String(socket.id) < String(peer.socketId)) void makeOffer(peer);
    };
    const onOffer = async ({ fromSocketId, userId, username, offer }) => {
      if (!enabledRef.current || !fromSocketId || !offer) return;
      const peer = {
        socketId: fromSocketId,
        userId,
        username,
        muted: peerStatesRef.current[fromSocketId]?.muted,
      };
      const pc = ensurePeer(peer);
      if (!pc) return;
      const entry = peersRef.current.get(fromSocketId);
      if (!entry) return;
      try {
        // Offer collision: we sent an offer at the same time. Roll ours back
        // so the remote offer can be applied instead of throwing.
        if (pc.signalingState === 'have-local-offer') {
          await pc.setLocalDescription({ type: 'rollback' });
        }
        await pc.setRemoteDescription(offer);
        entry.remoteDescriptionSet = true;
        await flushCandidates(fromSocketId);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socket.emit('voice:answer', { matchId, toSocketId: fromSocketId, answer: pc.localDescription });
      } catch (err) {
        recordFail(err);
        setVoiceError('Voice negotiation failed.');
      }
    };
    const onAnswer = async ({ fromSocketId, answer }) => {
      const entry = peersRef.current.get(fromSocketId);
      if (!entry || !answer) return;
      // Only an answer to our own pending offer can be applied. A late or
      // duplicate answer in any other state is stale and must be ignored.
      if (entry.pc.signalingState !== 'have-local-offer') return;
      try {
        await entry.pc.setRemoteDescription(answer);
        entry.remoteDescriptionSet = true;
        await flushCandidates(fromSocketId);
      } catch (err) {
        recordFail(err);
      }
    };
    const onIce = async ({ fromSocketId, candidate }) => {
      if (!candidate || fromSocketId === socket.id) return;
      const entry = peersRef.current.get(fromSocketId);
      if (!entry || !entry.remoteDescriptionSet) {
        const pending = pendingCandidatesRef.current.get(fromSocketId) || [];
        pending.push(candidate);
        pendingCandidatesRef.current.set(fromSocketId, pending);
        return;
      }
      try {
        await entry.pc.addIceCandidate(candidate);
      } catch (err) {
        recordFail(err);
      }
    };
    const onMute = ({ socketId, userId, muted: nextMuted }) => {
      if (socketId === socket.id) return;
      updatePeer(socketId, { userId, muted: !!nextMuted });
      notifyVoiceState(String(userId || peerStatesRef.current[socketId]?.userId || socketId), { muted: !!nextMuted, speaking: !!peerStatesRef.current[socketId]?.speaking, voiceEnabled: true });
    };
    const onSpeaking = ({ socketId, userId, speaking }) => {
      if (socketId === socket.id) return;
      updatePeer(socketId, { userId, speaking: !!speaking });
      notifyVoiceState(String(userId || peerStatesRef.current[socketId]?.userId || socketId), { muted: !!peerStatesRef.current[socketId]?.muted, speaking: !!speaking, voiceEnabled: true });
    };
    const onPeerLeft = ({ socketId }) => {
      if (socketId) {
        const peer = peerStatesRef.current[socketId];
        if (peer?.userId) notifyVoiceState(String(peer.userId), { muted: true, speaking: false, voiceEnabled: false });
        closePeer(socketId);
      }
    };

    socket.on('connect', onConnect);
    socket.on('voice:peers', onPeers);
    socket.on('voice:peer-ready', onReady);
    socket.on('voice:offer', onOffer);
    socket.on('voice:answer', onAnswer);
    socket.on('voice:ice', onIce);
    socket.on('voice:mute', onMute);
    socket.on('voice:speaking', onSpeaking);
    socket.on('voice:peer-left', onPeerLeft);

    if (socket.connected) emitReady();

    return () => {
      socket.off('connect', onConnect);
      socket.off('voice:peers', onPeers);
      socket.off('voice:peer-ready', onReady);
      socket.off('voice:offer', onOffer);
      socket.off('voice:answer', onAnswer);
      socket.off('voice:ice', onIce);
      socket.off('voice:mute', onMute);
      socket.off('voice:speaking', onSpeaking);
      socket.off('voice:peer-left', onPeerLeft);
    };
  }, [socket, matchId, active, connected, fetchIceServers, ensurePeer, makeOffer, flushCandidates, closePeer, updatePeer, refreshStatus, recordFail, notifyVoiceState]);

  useEffect(() => {
    if (!socket || !active) return undefined;
    const onConnectState = () => {
      connectedRef.current = true;
      if (enabledRef.current) {
        peersRef.current.forEach((_, socketId) => closePeer(socketId));
        setStatus('Connecting...');
        const now = Date.now();
        if (now - readyEmitRef.current > 250) {
          readyEmitRef.current = now;
          void fetchIceServers().finally(() => {
            if (socket.connected) socket.emit('voice:ready', { matchId, muted: mutedRef.current });
          });
        }
      }
    };
    const onDisconnectState = () => {
      connectedRef.current = false;
      if (!enabledRef.current) return;
      peersRef.current.forEach((_, socketId) => closePeer(socketId));
      setStatus('Reconnecting...');
    };
    socket.on('connect', onConnectState);
    socket.on('disconnect', onDisconnectState);
    return () => {
      socket.off('connect', onConnectState);
      socket.off('disconnect', onDisconnectState);
    };
  }, [socket, matchId, active, fetchIceServers, closePeer]);

  useEffect(() => {
    connectedRef.current = connected;
    if (!enabledRef.current) return;
    if (!connected) {
      peersRef.current.forEach((_, socketId) => closePeer(socketId));
      setStatus('Reconnecting...');
      return;
    }
    setStatus('Connecting...');
    if (socket?.connected) {
      const now = Date.now();
      if (now - readyEmitRef.current > 250) {
        readyEmitRef.current = now;
        void fetchIceServers().finally(() => {
          if (socket.connected) socket.emit('voice:ready', { matchId, muted: mutedRef.current });
        });
      }
    }
  }, [connected, socket, matchId, fetchIceServers, closePeer]);

  useEffect(() => {
    if (!active) return undefined;
    const onVis = async () => {
      if (document.visibilityState !== 'visible' || !enabledRef.current) return;
      const ctx = analyserRef.current?.ctx;
      if (ctx?.state === 'suspended') ctx.resume().catch((err) => recordFail(err));
      const track = streamRef.current?.getAudioTracks?.()[0];
      if (micWantedRef.current && track && track.readyState === 'ended') {
        await requestMic();
      }
      peersRef.current.forEach((entry, id) => {
        if (['failed', 'closed'].includes(entry.pc.connectionState)) void restartPeer(id);
      });
      remoteAudiosRef.current.forEach((audio) => tryPlay(audio));
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pageshow', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pageshow', onVis);
    };
  }, [active, requestMic, restartPeer, tryPlay, recordFail]);

  useEffect(() => {
    const currentIds = new Set((Array.isArray(players) ? players : []).map((p) => String(p.userId)));
    if (!currentIds.size) return;
    Object.entries(peerStates).forEach(([socketId, peer]) => {
      if (peer.userId && !currentIds.has(String(peer.userId))) closePeer(socketId);
    });
  }, [players, peerStates, closePeer]);

  useEffect(() => {
    const container = gameContainerRef?.current;
    if (!container || !active) return undefined;
    const onGamePointerDown = () => {
      resumeAudio();
      container.removeEventListener('pointerdown', onGamePointerDown, true);
    };
    container.addEventListener('pointerdown', onGamePointerDown, true);
    return () => container.removeEventListener('pointerdown', onGamePointerDown, true);
  }, [gameContainerRef, active, resumeAudio]);

  useEffect(() => {
    if (!popoverOpen) return undefined;
    const onPointerDown = (event) => {
      if (!event.target.closest?.('[data-whot-voice-ui="true"]')) setPopoverOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [popoverOpen]);

  useEffect(() => {
    return () => {
      unmountedRef.current = true;
      if (speakingTrailingRef.current) clearTimeout(speakingTrailingRef.current);
      if (speakingTimerRef.current) cancelAnimationFrame(speakingTimerRef.current);
      if (enabledRef.current || streamRef.current || peersRef.current.size) shutdown(true);
    };
  }, [shutdown]);

  const toggleMic = useCallback(async () => {
    if (hasMicRef.current && streamRef.current) {
      const track = streamRef.current.getAudioTracks?.()[0];
      peersRef.current.forEach((entry) => entry.audioSender?.replaceTrack(null).catch((err) => recordFail(err)));
      if (track) {
        track.onended = null;
        track.onmute = null;
        track.onunmute = null;
      }
      try { track?.stop(); } catch (err) { recordFail(err); }
      streamRef.current = null;
      hasMicRef.current = false;
      micWantedRef.current = false;
      setHasMic(false);
      mutedRef.current = true;
      setMuted(true);
      stopAnalyser();
      setVoiceError('');
      setMicMuted(false);
      micMutedRef.current = false;
      socket?.emit('voice:mute', { matchId, muted: true });
      notifyVoiceState(String(playerIdRef.current), { muted: true, speaking: false, voiceEnabled: true });
      return;
    }
    await startVoice();
  }, [matchId, notifyVoiceState, recordFail, socket, startVoice, stopAnalyser]);

  const compactState = !enabled
    ? 'Off'
    : !connected
      ? 'Connecting'
      : hasMic && !muted
        ? 'Mic on'
        : 'Mic off';

  const dotColor = compactState === 'Mic on' ? '#22c55e'
    : compactState === 'Connecting' ? '#f59e0b'
      : permissionError || voiceError ? '#ef4444'
        : '#6b7280';

  const statusForPopover = !enabled
    ? (permissionError || 'Voice chat is off.')
    : micMuted
      ? 'Microphone stopped or muted by the device.'
      : !hasMic || muted
        ? 'Listening'
        : 'Mic on';

  return (
    <div
      data-whot-voice-ui="true"
      style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 6 }}
      onPointerDown={resumeAudio}
    >
      <button
        type="button"
        aria-label={hasMic && !muted ? 'Turn microphone off' : 'Turn microphone on'}
        title={permissionError || statusForPopover}
        onClick={() => {
          resumeAudio();
          void toggleMic();
          if (compact) setPopoverOpen(true);
        }}
        style={{
          width: 42, height: 42, borderRadius: 12,
          border: `1px solid ${hasMic && !muted ? 'rgba(34,197,94,.45)' : 'rgba(255,255,255,.15)'}`,
          background: hasMic && !muted ? 'rgba(34,197,94,.12)' : 'rgba(255,255,255,.05)',
          color: '#fff', fontSize: 19, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: isSpeaking && hasMic && !muted
            ? '0 0 0 4px rgba(34,197,94,.20)'
            : hasMic && !muted ? '0 0 0 2px rgba(34,197,94,.10)' : 'none',
          position: 'relative',
        }}
      >
        {hasMic && !muted ? '🎙️' : '🔇'}
        <span
          aria-label={compactState}
          style={{
            position: 'absolute', top: 2, right: 2, width: 7, height: 7, borderRadius: '50%',
            background: dotColor,
            boxShadow: isSpeaking && hasMic && !muted ? '0 0 0 4px rgba(34,197,94,.20)' : 'none',
          }}
        />
      </button>

      {!compact && (
        <>
          <button
            type="button"
            onClick={() => void toggleMic()}
            title={statusForPopover}
            style={{ border: 0, background: 'transparent', color: '#9ca3af', fontSize: 10, cursor: 'pointer', padding: 2 }}
          >
            {compactState}
          </button>
          {enabled && isSpeaking && !muted && (
            <span aria-label="You are speaking" title="You are speaking" style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 0 4px rgba(34,197,94,.14)', display: 'inline-block' }} />
          )}
        </>
      )}

      {compact && (
        <span
          style={{ color: '#d1fae5', fontSize: 10, lineHeight: 1.1, fontWeight: 800, whiteSpace: 'nowrap', textAlign: 'center' }}
        >
          {compactState === 'Connecting' ? 'Connecting' : compactState}
        </span>
      )}

      {compact && popoverOpen && (
        <div
          data-whot-voice-ui="true"
          role="dialog"
          aria-label="Voice chat controls"
          style={{
            position: 'fixed',
            bottom: NAV_HEIGHT + 8,
            left: 'max(8px, min(calc(100vw - 268px), 50vw - 130px))',
            width: 'min(260px, calc(100vw - 16px))',
            maxWidth: 260,
            boxSizing: 'border-box',
            background: 'rgba(3,15,10,.97)',
            border: '1px solid rgba(167,243,208,.2)',
            borderRadius: 12,
            padding: 10,
            color: '#fff',
            zIndex: 300,
            boxShadow: '0 12px 35px rgba(0,0,0,.45)',
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>Voice Chat</div>
          <div role="status" style={{ fontSize: 11, color: dotColor, marginBottom: 8 }}>{statusForPopover}</div>
          {micMuted && <div style={{ fontSize: 10, color: '#fbbf24', marginBottom: 8 }}>Microphone stopped. Tap Enable mic to try again.</div>}
          {permissionError && <div style={{ fontSize: 10, color: '#fca5a5', marginBottom: 8 }}>{permissionError}</div>}
          {voiceError && voiceError !== permissionError && <div style={{ fontSize: 10, color: '#fca5a5', marginBottom: 8 }}>{voiceError}</div>}
          {audioBlocked && (
            <button type="button" onClick={resumeAudio} style={{ width: '100%', marginBottom: 6, padding: '8px 10px', borderRadius: 8, border: 0, background: '#f59e0b', color: '#111', fontWeight: 800 }}>
              Tap to enable sound
            </button>
          )}
          {enabled && (
            <>
              <button type="button" onClick={() => void toggleMic()} style={{ width: '100%', marginBottom: 6, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(167,243,208,.2)', background: 'rgba(255,255,255,.06)', color: '#fff', fontWeight: 700 }}>
                {hasMic && !muted ? 'Mic on' : 'Mic off'}
              </button>
              <button type="button" onClick={() => {
                const next = !speakerMuted;
                setSpeakerMuted(next);
                speakerMutedRef.current = next;
                remoteAudiosRef.current.forEach((audio) => { audio.muted = next; });
              }} style={{ width: '100%', marginBottom: 6, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(167,243,208,.2)', background: 'rgba(255,255,255,.06)', color: '#fff', fontWeight: 700 }}>
                {speakerMuted ? 'Unmute speaker' : 'Mute speaker'}
              </button>
            </>
          )}
          {voiceError.includes('Tap to retry') && (
            <button type="button" onClick={() => {
              Object.keys(peerStates).forEach((id) => restartCountRef.current.delete(id));
              Object.keys(peerStates).forEach((id) => rebuildPeer(id));
              setVoiceError('');
            }} style={{ width: '100%', marginBottom: 6, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(167,243,208,.2)', background: 'rgba(255,255,255,.06)', color: '#fff', fontWeight: 700 }}>
              Retry
            </button>
          )}
          {!Object.keys(peerStates).length && enabled && (
            <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 7 }}>Waiting for others</div>
          )}
        </div>
      )}
    </div>
  );
}

export function getVoiceStateForPlayer(peerStates, userId) {
  const found = Object.values(peerStates || {}).find((peer) => String(peer.userId) === String(userId));
  return found || {};
}
